import { AxiosPromise, AxiosRequestConfig } from 'axios';
import { BaseApi } from '@trukt/portal-utils/BaseApi';
import { BaseRequest } from '@trukt/types/BaseRequest';
import { NoteObjectType } from '../../components/Cards/InternalNotes/InternalNotes.types';
import { PubSubTokenResponse, GetJobNotesParams, JobNotesResponse } from './types';

import {
  GetAccountResponse,
  AccountTypeOptions,
  AccountServices,
  AccountServicesEndpoint,
  AttachmentServices,
  AttachmentEndpoint,
  BulkEndpoint,
  BulkSevices,
  InvoiceEndpoint,
  InvoiceServices,
  JobEndpoint,
  JobServices,
  LoadboardEndpoint,
  LoadboardSevices,
  OrderSevices,
  CaseEndpoint,
  CaseSevices,
  CommodityServices,
  CommodityServicesEndpoint,
  StopServices,
  StopServicesEndpoint,
  InspectionServices,
  InspectionServicesEndpoint,
} from './services';

import { OrderActivitiesResponse } from './truktApi.types';
import { TerminalServices, TerminalServicesEndpoint } from './services/TerminalServices';

class TruktApi extends BaseApi {
  bulk: BulkEndpoint;
  job: JobEndpoint;
  order: OrderSevices;
  loadboard: LoadboardEndpoint;
  attachments: AttachmentEndpoint;
  invoice: InvoiceEndpoint;
  account: AccountServicesEndpoint;
  case: CaseEndpoint;
  commodity: CommodityServicesEndpoint;
  terminal: TerminalServicesEndpoint;
  stop: StopServicesEndpoint;
  inspection: InspectionServicesEndpoint;

  constructor(config: AxiosRequestConfig) {
    super(config);
    this.bulk = new BulkSevices(this.instance);
    this.job = new JobServices(this.instance);
    this.order = new OrderSevices(this.instance);
    this.loadboard = new LoadboardSevices(this.instance);
    this.attachments = new AttachmentServices(this.instance);
    this.invoice = new InvoiceServices(this.instance);
    this.account = new AccountServices(this.instance);
    this.case = new CaseSevices(this.instance);
    this.commodity = new CommodityServices(this.instance);
    this.terminal = new TerminalServices(this.instance);
    this.stop = new StopServices(this.instance);
    this.inspection = new InspectionServices(this.instance);
  }

  async postNote({
    objectGuid,
    data,
    object = NoteObjectType.JOB,
  }: {
    objectGuid: string;
    data: any;
    object?: NoteObjectType;
  }): Promise<any> {
    return this.instance.post(`/note/${object}/${objectGuid}`, {
      ...data,
      body: data.body.trim(),
    });
  }

  async getJobNotes(
    jobGuid: string,
    params?: BaseRequest<GetJobNotesParams>['queryParams'],
  ): Promise<AxiosPromise<JobNotesResponse>> {
    return this.instance.get(`/job/${jobGuid}/notes`, {
      params,
    });
  }

  async getAccount(type: AccountTypeOptions, guid: string): Promise<GetAccountResponse | null> {
    if (!guid || !type) return null;

    const { data }: { data: Promise<GetAccountResponse> | null } = await this.instance.get(
      `/account/${type}/${guid}`,
    );

    return data;
  }

  async getAccounts(type: AccountTypeOptions, q: string): Promise<GetAccountResponse[]> {
    if (!q || q.trim().length < 3) return [];
    const { data }: { data: Promise<GetAccountResponse[]> } = await this.instance.get(
      `/account/${type}`,
      { params: { search: q, active: type === 'client' ? true : undefined } },
    );
    return data;
  }

  getOrderActivities(
    orderGuid: string,
    jobGuid: string,
    { queryParams }: Pick<BaseRequest<{ rc?: number; pg?: number }>, 'queryParams'> = {
      queryParams: {},
    },
  ): AxiosPromise<OrderActivitiesResponse> {
    return this.instance.get(`activitylogs/${orderGuid}/${jobGuid}`, {
      params: queryParams,
    });
  }

  getPubsubToken(): AxiosPromise<PubSubTokenResponse> {
    return this.instance.get('/pubsub/token');
  }
}

export const truktApi = new TruktApi({
  baseURL: import.meta.env.VITE_TRUKT_API_BASE_URL,
});
